import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Send, CheckCircle2, AlertCircle, HeartHandshake, ShieldCheck,
  Clock3, Award, Users, Upload, FileText, ArrowRight, Phone, ChevronLeft, Sparkles,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCall from "@/components/StickyCall";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createMonitorApplication } from "@/lib/api";
import { COMPANY } from "@/lib/data";

const initial = {
  // Basic
  full_name: "", phone: "", email: "", city_state: "",
  // Availability
  days_available: "", hours_available: "",
  morning_shifts: null, afternoon_shifts: null,
  start_availability: "",
  // Work Experience
  recent_employer: "", job_title: "", employment_from: "", employment_to: "",
  job_responsibilities: "", experience_with: [], additional_experience: "",
  // Requirements & Eligibility
  cpr_first_aid: "",
  willing_training: null,
  willing_background_check: null,
  willing_drug_screen: null,
  reliable_transportation: null,
  comfortable_assisting: null,
  // Consent
  consent: false,
  // Resume
  resume_base64: null, resume_filename: null,
};

const REQUIRED_TEXT = {
  full_name: "Full name is required",
  phone: "Phone is required",
  email: "Email is required",
  city_state: "City and state are required",
  days_available: "Please list days you are available",
  hours_available: "Please list hours you are available",
  start_availability: "Please choose when you can start",
  cpr_first_aid: "Please choose an option",
};
const REQUIRED_BOOL = [
  "morning_shifts", "afternoon_shifts",
  "willing_training", "willing_background_check", "willing_drug_screen",
  "reliable_transportation", "comfortable_assisting",
];

const EXPERIENCE_OPTIONS = [
  { value: "children_students", label: "Children / Students" },
  { value: "seniors", label: "Seniors" },
  { value: "disabilities", label: "Individuals with disabilities" },
  { value: "healthcare", label: "Healthcare / Caregiving" },
  { value: "transportation", label: "Transportation" },
  { value: "customer_service", label: "Customer Service" },
  { value: "none", label: "None of the above" },
];

const RESPONSIBILITIES = [
  "Accompany passengers during assigned transportation routes",
  "Assist with passenger supervision and safety throughout the ride",
  "Help passengers safely enter and exit the vehicle when needed",
  "Support passengers who may require additional supervision or assistance",
  "Help maintain a safe, calm, and comfortable environment",
  "Assist the driver with passenger safety and communication",
  "Monitor passengers and report any concerns or incidents",
  "Follow all company safety policies and procedures",
  "Communicate professionally with passengers, families, and team members",
  "Arrive on time and be dependable for assigned shifts",
];

const PERKS = [
  { icon: HeartHandshake, title: "Meaningful Work", body: "Make every ride safer and more comfortable for the passengers who need extra support." },
  { icon: Clock3, title: "Flexible Hours", body: "Morning, afternoon, and split shifts available — work around school routes or other commitments." },
  { icon: Award, title: "Paid Training", body: "Full onboarding on safety, communication, and passenger supervision — no experience required." },
  { icon: Users, title: "Team Environment", body: "Ride shotgun with a supportive team of drivers who care about their passengers." },
  { icon: ShieldCheck, title: "Safety First", body: "Modern, well-maintained vehicles and clear procedures so you always know what to do." },
  { icon: Sparkles, title: "Room to Grow", body: "Grow with a WMATC-certified operator serving Virginia, DC, and Maryland." },
];

export default function CareersMonitor() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const refs = useRef({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleExperience = (value) => {
    setForm((f) => {
      const has = f.experience_with.includes(value);
      let next;
      if (value === "none") {
        next = has ? [] : ["none"];
      } else {
        next = has ? f.experience_with.filter((x) => x !== value) : [...f.experience_with.filter((x) => x !== "none"), value];
      }
      return { ...f, experience_with: next };
    });
  };

  const validate = () => {
    const next = {};
    for (const [k, msg] of Object.entries(REQUIRED_TEXT)) {
      if (!form[k] || !String(form[k]).trim()) next[k] = msg;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Please enter a valid email";
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) next.phone = "Please enter a 10-digit phone number";

    for (const k of REQUIRED_BOOL) {
      if (form[k] !== true && form[k] !== false) next[k] = "Please select Yes or No";
    }
    if (!form.consent) next.consent = "Please confirm the consent statement to submit.";

    setErrors(next);
    return next;
  };

  const handleResume = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be smaller than 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(",")[1] || "";
      setForm((f) => ({ ...f, resume_base64: base64, resume_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      const firstKey = Object.keys(next)[0];
      const node = refs.current[firstKey];
      if (node && node.scrollIntoView) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => node.focus?.(), 350);
      }
      toast.error(`Please complete ${Object.keys(next).length} required field${Object.keys(next).length > 1 ? "s" : ""}.`);
      return;
    }
    setLoading(true);
    try {
      await createMonitorApplication(form);
      toast.success("Application submitted! We'll be in touch soon.");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Transportation Monitor / Passenger Attendant Job Application | Comfort Care"
        description="Apply now to become a Transportation Monitor / Passenger Attendant with Comfort Care Transportation. Ride along on student and medical transportation routes helping keep passengers safe. Flexible hours, paid training."
        path="/careers/transportation-monitor"
        keywords={["transportation monitor jobs Virginia", "passenger attendant jobs DC Maryland", "school bus monitor Virginia", "student transportation attendant", "Comfort Care careers"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            LOCAL_BUSINESS_SCHEMA,
            {
              "@type": "JobPosting",
              title: "Transportation Monitor / Passenger Attendant",
              description: "Accompany passengers during transportation routes (including student transportation) and assist in providing a safe, comfortable ride. Support drivers with passenger supervision and safety.",
              datePosted: new Date().toISOString().slice(0, 10),
              employmentType: ["FULL_TIME", "PART_TIME", "PER_DIEM"],
              hiringOrganization: {
                "@type": "Organization",
                name: "Comfort Care Transportation",
                sameAs: "https://comfortcarenemt.com",
                logo: COMPANY.logoUrl,
              },
              jobLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "4209 Evergreen Lane, Suite #202",
                  addressLocality: "Annandale",
                  addressRegion: "VA",
                  postalCode: "22003",
                  addressCountry: "US",
                },
              },
              applicantLocationRequirements: [
                { "@type": "State", name: "Virginia" },
                { "@type": "AdministrativeArea", name: "Washington, D.C." },
                { "@type": "State", name: "Maryland" },
              ],
              validThrough: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
              directApply: true,
            },
          ],
        }}
      />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="grain-bg py-16 md:py-24">
          <div className="container-pad mx-auto max-w-7xl">
            <Link to="/careers" data-testid="back-to-careers" className="inline-flex items-center gap-1 text-sm text-navy/60 hover:text-teal-600">
              <ChevronLeft className="w-4 h-4" /> All open positions
            </Link>
            <div className="grid lg:grid-cols-12 gap-12 mt-6 items-center">
              <div className="lg:col-span-7">
                <span className="eyebrow">
                  <HeartHandshake className="w-3.5 h-3.5" /> Position 2 of 2 · Now Hiring
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-navy tracking-tight mt-5 leading-[1.05]">
                  Transportation <strong className="font-bold text-teal-600">Monitor.</strong>
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl font-light">
                  Ride along with our drivers on student transportation and other routes as a
                  <strong className="text-navy"> Passenger Attendant</strong>. Help supervise, comfort,
                  and safely assist the passengers who need a little extra support.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#apply" className="btn-primary" data-testid="monitor-apply-cta">Apply Now <ArrowRight className="w-4 h-4" /></a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline">
                    <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                  </a>
                </div>
                <div className="mt-8 flex flex-wrap gap-4 text-sm text-navy/70">
                  <Tag>Competitive Pay</Tag>
                  <Tag>Flexible Hours</Tag>
                  <Tag>Paid Training</Tag>
                  <Tag>No Driving Required</Tag>
                </div>
              </div>
              <div className="lg:col-span-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l5bnk78a_2C3E0AE7-BC6B-41D5-8937-5C064B8E8692.PNG"
                  alt="Comfort Care Transportation vehicle"
                  className="rounded-3xl w-full h-[420px] object-cover shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Responsibilities */}
        <section className="section-pad">
          <div className="container-pad mx-auto max-w-5xl">
            <span className="eyebrow">
              <span className="w-8 h-px bg-teal-500" />
              What You&apos;ll Do
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
              A day as a <strong className="font-bold text-teal-600">passenger attendant.</strong>
            </h2>
            <p className="text-navy/70 mt-4 max-w-3xl">
              Transportation Monitors accompany passengers during assigned routes and assist in providing a
              safe, comfortable, and positive experience. You may work alongside drivers on student routes
              and other assigned trips.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {RESPONSIBILITIES.map((r) => (
                <div key={r} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100">
                  <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <span className="text-navy/85 text-sm">{r}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Perks */}
        <section className="section-pad bg-sand">
          <div className="container-pad mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="eyebrow justify-center">
                <span className="w-8 h-px bg-teal-500" />
                What We Offer
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
                Rewarding work, <strong className="font-bold text-teal-600">supportive team.</strong>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 rounded-3xl overflow-hidden border border-gray-100">
              {PERKS.map((p) => (
                <div key={p.title} className="bg-white p-8 hover:bg-sand transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-600">
                    <p.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mt-5">{p.title}</h3>
                  <p className="text-navy/65 mt-2 leading-relaxed text-sm">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application */}
        <section id="apply" className="section-pad">
          <div className="container-pad mx-auto max-w-4xl">
            {submitted ? (
              <SuccessCard />
            ) : (
              <form
                onSubmit={submit}
                noValidate
                data-testid="monitor-application-form"
                className="bg-white rounded-3xl p-7 md:p-10 lg:p-12 border border-gray-100 shadow-[0_30px_80px_-30px_rgba(10,25,47,0.2)]"
              >
                <div className="text-center mb-8 pb-6 border-b border-gray-100">
                  <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest">
                    Transportation Monitor
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-4">Passenger Attendant Application</h2>
                  <p className="text-navy/65 mt-3">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. Takes ~3 minutes.</p>
                </div>

                <FormErrorBanner errors={errors} />

                {/* BASIC INFORMATION */}
                <Section title="Basic Information">
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Full Name" required error={errors.full_name}>
                      <Input ref={(el) => (refs.current.full_name = el)} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls(errors.full_name)} />
                    </Field>
                    <Field label="Phone Number" required error={errors.phone}>
                      <Input ref={(el) => (refs.current.phone = el)} type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls(errors.phone)} />
                    </Field>
                    <Field label="Email Address" required error={errors.email}>
                      <Input ref={(el) => (refs.current.email = el)} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} />
                    </Field>
                    <Field label="City and State" required error={errors.city_state}>
                      <Input ref={(el) => (refs.current.city_state = el)} value={form.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="Annandale, VA" className={inputCls(errors.city_state)} />
                    </Field>
                  </div>
                </Section>

                {/* AVAILABILITY */}
                <Section title="Availability">
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="What days are you available to work?" required error={errors.days_available}>
                      <Input ref={(el) => (refs.current.days_available = el)} value={form.days_available} onChange={(e) => set("days_available", e.target.value)} placeholder="e.g. Mon-Fri, weekends" className={inputCls(errors.days_available)} />
                    </Field>
                    <Field label="What hours are you generally available?" required error={errors.hours_available}>
                      <Input ref={(el) => (refs.current.hours_available = el)} value={form.hours_available} onChange={(e) => set("hours_available", e.target.value)} placeholder="e.g. 6am–3pm" className={inputCls(errors.hours_available)} />
                    </Field>
                  </div>
                  <YesNo label="Are you available for morning shifts?" value={form.morning_shifts} onChange={(v) => set("morning_shifts", v)} error={errors.morning_shifts} refSet={(el) => (refs.current.morning_shifts = el)} required />
                  <YesNo label="Are you available for afternoon shifts?" value={form.afternoon_shifts} onChange={(v) => set("afternoon_shifts", v)} error={errors.afternoon_shifts} refSet={(el) => (refs.current.afternoon_shifts = el)} required />

                  <Field label="How soon are you available to start?" required error={errors.start_availability}>
                    <Select value={form.start_availability} onValueChange={(v) => set("start_availability", v)}>
                      <SelectTrigger ref={(el) => (refs.current.start_availability = el)} className={`h-12 text-base ${errors.start_availability ? "border-red-400" : ""}`} data-testid="start-availability-select">
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediately" className="text-base py-2.5">Immediately</SelectItem>
                        <SelectItem value="within_1_week" className="text-base py-2.5">Within 1 week</SelectItem>
                        <SelectItem value="within_2_weeks" className="text-base py-2.5">Within 2 weeks</SelectItem>
                        <SelectItem value="more_than_2_weeks" className="text-base py-2.5">More than 2 weeks</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </Section>

                {/* WORK EXPERIENCE */}
                <Section title="Work Experience">
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Most recent or current employer">
                      <Input value={form.recent_employer} onChange={(e) => set("recent_employer", e.target.value)} className="h-12 text-base" />
                    </Field>
                    <Field label="Job title / position">
                      <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="h-12 text-base" />
                    </Field>
                    <Field label="Employment dates — From">
                      <Input value={form.employment_from} onChange={(e) => set("employment_from", e.target.value)} placeholder="e.g. Jan 2023" className="h-12 text-base" />
                    </Field>
                    <Field label="Employment dates — To">
                      <Input value={form.employment_to} onChange={(e) => set("employment_to", e.target.value)} placeholder="e.g. Present" className="h-12 text-base" />
                    </Field>
                  </div>
                  <Field label="Please briefly describe your primary job responsibilities">
                    <Textarea value={form.job_responsibilities} onChange={(e) => set("job_responsibilities", e.target.value)} rows={3} className="text-base rounded-xl" />
                  </Field>

                  <Field label="Do you have experience working with any of the following? (select all that apply)">
                    <div className="grid sm:grid-cols-2 gap-2 mt-1">
                      {EXPERIENCE_OPTIONS.map((opt) => {
                        const checked = form.experience_with.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            data-testid={`exp-opt-${opt.value}`}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition text-sm font-medium ${
                              checked ? "border-teal-500 bg-teal-500/10 text-teal-700" : "border-gray-200 hover:border-teal-300 text-navy/80"
                            }`}
                          >
                            <Checkbox checked={checked} onCheckedChange={() => toggleExperience(opt.value)} />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label="Please briefly describe any additional experience you feel is relevant to this position (optional)">
                    <Textarea value={form.additional_experience} onChange={(e) => set("additional_experience", e.target.value)} rows={3} className="text-base rounded-xl" />
                  </Field>
                </Section>

                {/* REQUIREMENTS & ELIGIBILITY */}
                <Section title="Requirements & Eligibility">
                  <Field label="Are you currently CPR and First Aid certified?" required error={errors.cpr_first_aid}>
                    <RadioGroup
                      value={form.cpr_first_aid}
                      onValueChange={(v) => set("cpr_first_aid", v)}
                      className="grid gap-3 mt-1"
                    >
                      <CPRRadio value="certified" current={form.cpr_first_aid} label="Yes — I am currently CPR and First Aid certified" />
                      <CPRRadio value="willing_to_certify" current={form.cpr_first_aid} label="No, but I am willing to become certified" />
                    </RadioGroup>
                    <div ref={(el) => (refs.current.cpr_first_aid = el)} />
                  </Field>

                  <YesNo label="Are you willing to complete any required training before starting?" value={form.willing_training} onChange={(v) => set("willing_training", v)} error={errors.willing_training} refSet={(el) => (refs.current.willing_training = el)} required />
                  <YesNo label="Are you willing to undergo a background check, if required?" value={form.willing_background_check} onChange={(v) => set("willing_background_check", v)} error={errors.willing_background_check} refSet={(el) => (refs.current.willing_background_check = el)} required />
                  <YesNo label="Are you willing to undergo drug testing, if required?" value={form.willing_drug_screen} onChange={(v) => set("willing_drug_screen", v)} error={errors.willing_drug_screen} refSet={(el) => (refs.current.willing_drug_screen = el)} required />
                  <YesNo label="Do you have reliable transportation to get to and from your assigned work location?" value={form.reliable_transportation} onChange={(v) => set("reliable_transportation", v)} error={errors.reliable_transportation} refSet={(el) => (refs.current.reliable_transportation = el)} required />
                  <YesNo label="Are you comfortable working with and assisting passengers who may require additional supervision or support during transportation?" value={form.comfortable_assisting} onChange={(v) => set("comfortable_assisting", v)} error={errors.comfortable_assisting} refSet={(el) => (refs.current.comfortable_assisting = el)} required />
                </Section>

                {/* RESUME */}
                <Section title="Resume (optional)">
                  <Field label="Upload Resume (PDF/DOC, max 5 MB)">
                    <label className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 hover:border-teal-400 rounded-xl cursor-pointer transition bg-sand/40">
                      <Upload className="w-5 h-5 text-teal-500" />
                      <span className="text-navy/70 text-sm">
                        {form.resume_filename || "Click to upload your resume"}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleResume(e.target.files?.[0])}
                      />
                    </label>
                    {form.resume_filename && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-teal-700 bg-teal-500/10 px-3 py-2 rounded-lg">
                        <FileText className="w-4 h-4" />
                        {form.resume_filename}
                        <button type="button" onClick={() => set("resume_base64", null) || set("resume_filename", null)} className="ml-auto text-navy/50 hover:text-red-600 text-xs">Remove</button>
                      </div>
                    )}
                  </Field>
                </Section>

                {/* Consent */}
                <div className={`mt-8 p-5 rounded-xl border ${errors.consent ? "border-red-300 bg-red-50/50" : "border-gray-200 bg-sand"}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox id="monitor-consent" ref={(el) => (refs.current.consent = el)} checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" />
                    <label htmlFor="monitor-consent" className="text-sm text-navy/85 leading-relaxed cursor-pointer">
                      <strong className="text-navy">Consent Statement.</strong> By submitting this application, I certify that the information provided is accurate and complete.
                      I understand that employment may be contingent upon successful completion of required screenings, including background checks
                      and drug testing as applicable.
                    </label>
                  </div>
                  {errors.consent && (
                    <div className="text-red-600 text-sm mt-2 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.consent}
                    </div>
                  )}
                </div>

                <button type="submit" data-testid="monitor-submit-btn" disabled={loading} className="btn-primary mt-8 w-full !h-14 !text-base disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8 pt-8 first:mt-0 first:pt-0 first:border-0 border-t border-gray-100">
      <h3 className="font-heading text-lg font-semibold text-navy mb-5 flex items-center gap-2">
        <span className="w-1 h-5 bg-teal-500 rounded-full" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="mt-4 first:mt-0">
      <Label className="text-navy font-semibold text-sm flex items-center gap-1">
        {label}
        {required && <span className="text-red-500" aria-label="required">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-red-600 text-sm font-medium">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

function YesNo({ label, value, onChange, error, refSet, required }) {
  return (
    <div className="mt-5" ref={refSet}>
      <Label className="text-navy font-semibold text-sm flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <RadioGroup
        value={value === true ? "yes" : value === false ? "no" : ""}
        onValueChange={(v) => onChange(v === "yes")}
        className="grid grid-cols-2 gap-3 mt-2"
      >
        <YNOption value="yes" current={value === true} label="Yes" />
        <YNOption value="no" current={value === false} label="No" />
      </RadioGroup>
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-red-600 text-sm font-medium">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

function YNOption({ value, current, label }) {
  return (
    <label
      className={`flex items-center justify-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition font-medium text-base ${
        current ? "border-teal-500 bg-teal-500/10 text-teal-700" : "border-gray-200 hover:border-teal-300 text-navy/80"
      }`}
    >
      <RadioGroupItem value={value} />
      {label}
    </label>
  );
}

function CPRRadio({ value, current, label }) {
  const active = current === value;
  return (
    <label
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition font-medium text-sm ${
        active ? "border-teal-500 bg-teal-500/10 text-teal-700" : "border-gray-200 hover:border-teal-300 text-navy/80"
      }`}
    >
      <RadioGroupItem value={value} />
      {label}
    </label>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/70 backdrop-blur border border-gray-200 rounded-full px-4 py-1.5 text-xs font-semibold text-navy">
      <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
      {children}
    </span>
  );
}

function FormErrorBanner({ errors }) {
  const count = Object.keys(errors).length;
  if (count === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-6">
      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
      <div>
        <div className="text-red-900 font-semibold">Please complete the highlighted fields below.</div>
        <div className="text-red-700 text-sm mt-1">{count} field{count > 1 ? "s" : ""} need{count > 1 ? "" : "s"} your attention.</div>
      </div>
    </div>
  );
}

function SuccessCard() {
  return (
    <div data-testid="monitor-application-success" className="bg-white rounded-3xl p-10 md:p-14 border border-teal-100 text-center shadow-xl">
      <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-teal-600" />
      </div>
      <h3 className="font-heading text-3xl font-semibold text-navy mt-6">Application Received</h3>
      <p className="text-navy/70 mt-3 max-w-md mx-auto text-lg">
        Thank you for applying to Comfort Care Transportation as a Passenger Attendant. Our hiring team
        will review your application and reach out within 5 business days if it&apos;s a fit.
      </p>
      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Link to="/careers" className="btn-outline">See other positions</Link>
        <Link to="/" className="btn-secondary">Back to Home</Link>
      </div>
    </div>
  );
}

function inputCls(error) {
  return `h-12 text-base ${error ? "border-red-400 focus-visible:ring-red-400 bg-red-50/40" : "border-gray-200 focus-visible:ring-teal-500"}`;
}
